import { useEffect } from 'react';
import { match } from 'ts-pattern';

import { Button, Icon, Stack } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import {
  MicrophonePermissionState,
  useMicrophonePermission,
} from '@eluve/mic-utils';

interface MicrophoneRequestAlertProps {
  onPermissionStateChange?: (state: MicrophonePermissionState) => void;
  onRequestPermission?: () => void;
}

export const MicrophoneRequestAlert: React.FC<MicrophoneRequestAlertProps> = ({
  onPermissionStateChange,
  onRequestPermission,
}) => {
  const { t } = useTranslation();
  const { state: microphonePermissionStatus, checkNavigatorPermissions } =
    useMicrophonePermission();

  useEffect(() => {
    onPermissionStateChange?.(microphonePermissionStatus);
  }, [microphonePermissionStatus, onPermissionStateChange]);

  const requestMicrophoneAccess = async () => {
    const device = await navigator.mediaDevices.getUserMedia({ audio: true });

    checkNavigatorPermissions();

    device.getTracks().forEach((track) => {
      track.stop();
    });
  };

  return match(microphonePermissionStatus)
    .with('denied', () => (
      <Stack
        className="rounded-lg border-border-negative-subtle bg-bg-negative-subtle px-8 py-4 text-text-negative-subtle"
        align="center"
        gap={2}
        justify="center"
      >
        <Icon name="TriangleAlert" size="xl" />
        {t('microphone.permissionsDeniedTitle')}
        <span className="text-center text-xs">
          {t('microphone.permissionsDeniedMessage')}
        </span>
      </Stack>
    ))
    .with('no-microphone-devices', () => (
      <Stack
        className="rounded-lg border-border-warning-subtle bg-bg-warning-subtle px-8 py-4 text-text-warning-subtle"
        align="center"
        gap={2}
        justify="center"
      >
        <Icon name="TriangleAlert" size="xl" />
        {t('microphone.noDevicesTitle')}
        <span className="text-center text-xs">
          {t('microphone.noDevicesMessage')}
        </span>
        <Button
          onClick={onRequestPermission ?? requestMicrophoneAccess}
          variant="secondary"
          size="sm"
          className="cursor-pointer"
        >
          {t('microphone.retryPermission')}
        </Button>
      </Stack>
    ))
    .with('prompt', () => (
      <Stack
        data-accessibility-label="microphone-request-alert"
        className="rounded-lg border-border-warning-subtle bg-bg-warning-subtle px-8 py-4 text-text-warning-subtle"
        align="center"
        gap={2}
        justify="center"
      >
        <Icon name="TriangleAlert" size="xl" />
        {t('microphone.permissionsRequiredTitle')}
        <span className="text-center text-xs">
          {t('microphone.permissionsRequiredMessage')}
        </span>
        <Button
          onClick={onRequestPermission ?? requestMicrophoneAccess}
          size="sm"
          className="cursor-pointer"
        >
          {t('microphone.requestPermission')}
        </Button>
      </Stack>
    ))
    .otherwise(() => null);
};
